import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import theme from '../../pagesv2/theme';
import MeetingStatusBadge from '../MyMeetings/MeetingStatusBadge';

const RECENT_LIMIT = 3;

const WidgetCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.sm};
`;

const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
`;

const WidgetTitle = styled.h3`
  margin: 0;
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
`;

const ViewAllLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  font-family: inherit;
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const MeetingRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${theme.spacing.sm} 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  border-bottom: 1px solid ${theme.colors.borderLight};

  &:last-child { border-bottom: none; }

  &:hover {
    background: ${theme.colors.surfaceHover};
  }
`;

const MeetingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const MeetingTitle = styled.span`
  font-size: ${theme.typography.body.size};
  font-weight: 500;
  color: ${theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MeetingMeta = styled.span`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
`;

const formatRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < 0) return 'upcoming';
  const min = Math.round(diff / 60_000);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(diff / 3_600_000);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(diff / 86_400_000);
  return `${day}d ago`;
};

const RecentMeetingsWidget = observer(({ companyStore, myMeetingsStore }) => {
  const history = useHistory();
  const companyId = companyStore?.company?.id;

  useEffect(() => {
    if (companyId && !myMeetingsStore.lastLoadedAt) {
      myMeetingsStore.loadMeetings(companyId).catch(() => {});
    }
  }, [companyId, myMeetingsStore]);

  const meetings = myMeetingsStore.meetings.slice(0, RECENT_LIMIT);

  // Hide widget entirely when user has no meetings — matches the
  // "Live Meetings happen out of band" design (don't push it on users who don't use it)
  if (meetings.length === 0) return null;

  return (
    <WidgetCard>
      <WidgetHeader>
        <WidgetTitle>Recent Meetings</WidgetTitle>
        <ViewAllLink onClick={() => history.push('/live-meetings')}>View all →</ViewAllLink>
      </WidgetHeader>
      {meetings.map(m => (
        <MeetingRow
          key={m.botId}
          onClick={() => history.push(`/live-meetings#meeting-${m.botId}`)}
          type="button"
        >
          <MeetingInfo>
            <MeetingTitle>{m.meetingTitle || 'Untitled Meeting'}</MeetingTitle>
            <MeetingMeta>{formatRelative(m.meetingDate)}</MeetingMeta>
          </MeetingInfo>
          <MeetingStatusBadge status={m.status} />
        </MeetingRow>
      ))}
    </WidgetCard>
  );
});

export default RecentMeetingsWidget;
